import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { usePermissionGuard } from '../../utils/permissionGuard';

// Static navigation that never changes
const STATIC_ORG_NAVIGATION = [
  /*{
    name: 'Org Overview',
    path: '/dashboard',
    icon: (
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
          <g fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12.76c0-1.358 0-2.037.274-2.634c.275-.597.79-1.038 1.821-1.922l1-.857C9.96 5.75 10.89 4.95 12 4.95s2.041.799 3.905 2.396l1 .857c1.03.884 1.546 1.325 1.82 1.922c.275.597.275 1.276.275 2.634V17c0 1.886 0 2.828-.586 3.414S16.886 21 15 21H9c-1.886 0-2.828 0-3.414-.586S5 18.886 5 17z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.5 21v-5a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v5" />
          </g>
        </svg>
      </div>
    ),
  },*/
  {
    name: 'Organization Management',
    path: '/admin',
    icon: (
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
          <g fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="square" d="M12 15V9m3 3H9" />
          </g>
        </svg>
      </div>
    ),
  },
  {
    name: 'Student Management',
    path: '/students',
    requiresPermission: {
      resource: 'student',
      action: 'read',
      organizationWide: true // Only show for organization-wide access
    },
    icon: (
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
          <g fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M13.172 3H9c-1.886 0-2.828 0-3.414.586S5 5.114 5 7v10c0 1.886 0 2.828.586 3.414S7.114 21 9 21h6c1.886 0 2.828 0 3.414-.586S19 18.886 19 17V8.828c0-.408 0-.613-.076-.796s-.22-.329-.51-.618l-3.828-3.828c-.29-.29-.434-.434-.617-.51C13.785 3 13.58 3 13.172 3Z" />
            <path d="M13 3v4c0 .943 0 1.414.293 1.707S14.057 9 15 9h4" />
          </g>
        </svg>
      </div>
    ),
  },
];

const STATIC_SCHOOL_NAVIGATION = {
  analytics: [
    {
      name: 'School Overview',
      path: '/school-overview',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
            </g>
          </svg>
        </div>
      ),
    },
    {
      name: 'Student Tracking',
      path: '/student-tracking',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </g>
          </svg>
        </div>
      ),
    },
    {
      name: 'Class Tracking',
      path: '/class-tracking',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
              <circle cx="12" cy="8" r="2" />
            </g>
          </svg>
        </div>
      ),
    },
  ],
  management: [
    {
      name: 'Class Management',
      path: '/class-management',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <rect width="7" height="7" x="3" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="3" rx="1" />
              <rect width="7" height="7" x="14" y="14" rx="1" />
              <rect width="7" height="7" x="3" y="14" rx="1" />
            </g>
          </svg>
        </div>
      ),
    },
    {
      name: 'Student Management',
      path: '/students',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </g>
          </svg>
        </div>
      ),
    },
  ],
  reports: [
    {
      name: 'Take Attendance',
      path: '/take-attendance',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 8c.93 0 1.395 0 1.776.102a3 3 0 0 1 2.122 2.121C20 10.606 20 11.07 20 12v6a2 2 0 1 1-4 0V7.2c0-1.12 0-1.68-.218-2.108a2 2 0 0 0-.874-.874C14.48 4 13.92 4 12.8 4H7.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C4 5.52 4 6.08 4 7.2v9.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874C5.52 20 6.08 20 7.2 20H18" />
              <path strokeLinejoin="round" d="M12 8H8v4h4z" />
              <path strokeLinecap="round" d="M8 16h4" />
            </g>
          </svg>
        </div>
      ),
    },
    {
      name: 'Attendance Reports',
      path: '/attendance-reports',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2v6a2 2 0 0 0 2 2h6" />
              <path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2Z" />
              <path d="M9 15h6" />
              <path d="M9 11h1" />
            </g>
          </svg>
        </div>
      ),
    },
    /*{
      name: 'Import Attendance',
      path: '/import-attendance',
      icon: (
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="w-5 h-5 text-white" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2v6a2 2 0 0 0 2 2h6" />
              <path d="M15 18v-6m-3 3h6" />
              <path d="M4 22h14a2 2 0 0 0 2-2V7l-5-5H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2Z" />
            </g>
          </svg>
        </div>
      ),
    },*/
  ],
};

// Sidebar data manager
class SidebarDataManager {
  static STORAGE_KEYS = {
    ORGANIZATION: 'sidebar_organization',
    SCHOOLS: 'sidebar_schools',
    SELECTED_SCHOOL: 'sidebar_selected_school',
    LAST_UPDATE: 'sidebar_last_update'
  };

  static getCachedData() {
    try {
      const organization = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.ORGANIZATION) || 'null');
      const schools = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.SCHOOLS) || '[]');
      const selectedSchool = JSON.parse(localStorage.getItem(this.STORAGE_KEYS.SELECTED_SCHOOL) || 'null');
      
      return { organization, schools, selectedSchool };
    } catch (error) {
      console.warn('Error reading sidebar cache:', error);
      return { organization: null, schools: [], selectedSchool: null };
    }
  }

  static setCachedData(organization, schools, selectedSchool) {
    try {
      localStorage.setItem(this.STORAGE_KEYS.ORGANIZATION, JSON.stringify(organization));
      localStorage.setItem(this.STORAGE_KEYS.SCHOOLS, JSON.stringify(schools));
      localStorage.setItem(this.STORAGE_KEYS.SELECTED_SCHOOL, JSON.stringify(selectedSchool));
      localStorage.setItem(this.STORAGE_KEYS.LAST_UPDATE, Date.now().toString());
    } catch (error) {
      console.warn('Error writing sidebar cache:', error);
    }
  }

  static updateSelectedSchool(school) {
    try {
      localStorage.setItem(this.STORAGE_KEYS.SELECTED_SCHOOL, JSON.stringify(school));
    } catch (error) {
      console.warn('Error updating selected school:', error);
    }
  }
}

// Hook to subscribe to sidebar data updates
export const useSidebarData = () => {
  const [sidebarData, setSidebarData] = useState(() => SidebarDataManager.getCachedData());

  useEffect(() => {
    // Listen for storage changes (when updated from TenantContext)
    const handleStorageChange = (e) => {
      if (e.key?.startsWith('sidebar_')) {
        setSidebarData(SidebarDataManager.getCachedData());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events for same-window updates
    const handleSidebarUpdate = () => {
      setSidebarData(SidebarDataManager.getCachedData());
    };

    window.addEventListener('sidebarUpdate', handleSidebarUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('sidebarUpdate', handleSidebarUpdate);
    };
  }, []);

  return sidebarData;
};

// Function to update sidebar data from TenantContext
export const updateSidebarData = (organization, schools, selectedSchool) => {
  SidebarDataManager.setCachedData(organization, schools, selectedSchool);
  // Dispatch custom event for same-window updates
  window.dispatchEvent(new CustomEvent('sidebarUpdate'));
};

// Function to update just the selected school
export const updateSelectedSchool = (school) => {
  SidebarDataManager.updateSelectedSchool(school);
  window.dispatchEvent(new CustomEvent('sidebarUpdate'));
};

const StaticSidebar = ({ sidebarOpen, toggleSidebar }) => {
  const location = useLocation();
  const { organization, schools, selectedSchool } = useSidebarData();
  const { user } = useUserPermissions();
  const permissions = usePermissionGuard(user);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Generate different colors for school icons
  const getSchoolIconColor = (schoolId) => {
    const colors = [
      //'bg-blue-600',
      'bg-green-600', 
      'bg-purple-600',
      'bg-orange-600',
      'bg-red-600',
      'bg-indigo-600',
      'bg-pink-600',
      'bg-teal-600',
      'bg-yellow-600',
      'bg-gray-600'
    ];
    // Use school ID to consistently assign same color to same school
    const hash = schoolId ? schoolId.split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0) : 0;
    return colors[Math.abs(hash) % colors.length];
  };

  // Filter navigation based on permissions
  const filteredOrgNavigation = useMemo(() => {
    if (!user?.role) return STATIC_ORG_NAVIGATION;
    
    const filtered = STATIC_ORG_NAVIGATION.filter(item => {
      if (item.requiresPermission) {
        const { resource, action, organizationWide } = item.requiresPermission;
        
        // Check if organization-wide access is required
        if (organizationWide) {
          return permissions.hasOrganizationAccess(resource, action);
        } else {
          return permissions.hasPermission(resource, action);
        }
      }
      return true; // Show items without permission requirements
    });
    
    return filtered;
  }, [user, permissions]);

  // Build navigation URLs with cached organization data
  const buildNavigation = useMemo(() => {
    if (!organization) {
      return { orgNavigation: filteredOrgNavigation, schoolNavigation: STATIC_SCHOOL_NAVIGATION };
    }

    const orgNavigation = filteredOrgNavigation.map(item => ({
      ...item,
      href: `/org/${organization.unique_id}${item.path}`
    }));

    const schoolNavigation = {
      analytics: STATIC_SCHOOL_NAVIGATION.analytics.map(item => ({
        ...item,
        href: selectedSchool 
          ? `/org/${organization.unique_id}/school/${selectedSchool.unique_id || selectedSchool.unique_id}${item.path}`
          : '#'
      })),
      management: STATIC_SCHOOL_NAVIGATION.management.map(item => ({
        ...item,
        href: selectedSchool 
          ? `/org/${organization.unique_id}/school/${selectedSchool.unique_id || selectedSchool.unique_id}${item.path}`
          : '#'
      })),
      reports: STATIC_SCHOOL_NAVIGATION.reports.map(item => ({
        ...item,
        href: selectedSchool 
          ? `/org/${organization.unique_id}/school/${selectedSchool.unique_id || selectedSchool.unique_id}${item.path}`
          : '#'
      }))
    };

    return { orgNavigation, schoolNavigation };
  }, [organization, selectedSchool, filteredOrgNavigation]);

  const is_activePath = (path, href) => {
    if (!href || href === '#') return false;
    return location.pathname === href || location.pathname.startsWith(href);
  };

  const handleSchoolClick = (school) => {
    updateSelectedSchool(school);
    // Navigate to school dashboard
    if (organization && school) {
      const schoolId = school.unique_id || school.unique_id;
      window.location.href = `/org/${organization.unique_id}/school/${schoolId}/dashboard`;
    }
  };

  return (
    <>
      {/* Mobile overlay - only show on small screens */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}
      
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 overflow-y-auto transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
      {/* Logo Header */}
      <div className="flex items-center h-16 px-6 border-b border-gray-200">
        <Link 
          to={organization ? `/org/${organization.unique_id}/dashboard` : "/"} 
          className="flex items-center"
        >
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span className="ml-3 text-xl font-semibold text-gray-900">ArcAttend</span>
        </Link>
        <div className="mt-1">
          <span className="ml-3 text-sm font-semibold text-gray-400">Alpha 1.0.0</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="mt-6 px-0">
        {/* Organization Dashboard Section */}
        <div className="mb-6 px-3">
          <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Organization Dashboard
          </h3>
          <ul className="space-y-1">
            {buildNavigation.orgNavigation.map((item) => (
              <li key={item.name}>
                <Link
                  to={item.href || item.path}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    is_activePath(item.path, item.href)
                      ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {item.icon}
                  <span className="ml-3">{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Divider */}
        <div className="mb-3">
          <div className="border-t border-gray-200"></div>
        </div>

        {/* School Dashboard Section */}
        {schools.length > 0 && (
          <div className="mb-6">
            <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              School Dashboard
            </h3>
            
            {/* School Selector - Custom Dropdown */}
            <div className="mb-2 px-3">
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50  transition-colors duration-200 text-sm font-medium text-gray-700"
                >
                  <div className="flex items-center">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center mr-3 ${selectedSchool ? getSchoolIconColor(selectedSchool.id) : 'bg-gray-400'}`}>
                      <span className="text-white text-xs font-semibold">
                        {selectedSchool?.name?.charAt(0).toUpperCase() || 'S'}
                      </span>
                    </div>
                    <span className="truncate">
                      {selectedSchool?.name || 'Select School'}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <svg 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Dropdown Menu */}
                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                    {schools.map((school) => (
                      <button
                        key={school.id}
                        onClick={() => {
                          handleSchoolClick(school);
                          setDropdownOpen(false);
                        }}
                        className={`w-full flex items-center px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors duration-150 ${
                          selectedSchool?.id === school.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-md flex items-center justify-center mr-3 ${getSchoolIconColor(school.id)}`}>
                          <span className="text-white text-xs font-semibold">
                            {school.name?.charAt(0).toUpperCase() || 'S'}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="truncate block">{school.name}</span>
                        </div>
                        <div className="flex items-center">
                          {selectedSchool?.id === school.id && (
                            <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                    
                    {/* Divider */}
                    <div className="border-t border-gray-200 my-1"></div>
                    
                    {/* Create New School Option */}
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        window.location.href = organization ? `/org/${organization.unique_id}/admin#schools` : '/admin#schools';
                      }}
                      className="w-full flex items-center px-3 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors duration-150 text-gray-700"
                    >
                      <div className="w-6 h-6 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center mr-3">
                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                      <span className="text-gray-600">Create new</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Click outside to close dropdown */}
              {dropdownOpen && (
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setDropdownOpen(false)}
                />
              )}
            </div>

            {/* Divider */}
                <div className="mb-5">
                  <div className="border-t border-gray-200"></div>
                </div>

            {/* School Navigation */}
            {selectedSchool && (
              <div className="space-y-6 px-3">
                {/* Management Section */}
                <div>
                  <h4 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Management
                  </h4>
                  <ul className="space-y-1">
                    {buildNavigation.schoolNavigation.management.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            is_activePath(item.path, item.href)
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {item.icon}
                          <span className="ml-3">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                

                {/* Reports Section */}
                <div>
                  <h4 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Reports
                  </h4>
                  <ul className="space-y-1">
                    {buildNavigation.schoolNavigation.reports.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            is_activePath(item.path, item.href)
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {item.icon}
                          <span className="ml-3">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Analytics Section */}
                <div>
                  <h4 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Analytics
                  </h4>
                  <ul className="space-y-1">
                    {buildNavigation.schoolNavigation.analytics.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            is_activePath(item.path, item.href)
                              ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {item.icon}
                          <span className="ml-3">{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>
    </div>
    </>
  );
};

export default StaticSidebar;
